import React, { useEffect, useState } from 'react';
import styles from './LoadState.module.css';

/**
 * true 가 된 뒤 delay(ms)가 지나야 true 를 돌려준다.
 * 응답이 빨리 오면 로딩 표시가 번쩍였다 사라지는 것을 막는다.
 */
const useDelayedFlag = (active, delay) => {
    const [visible, setVisible] = useState(active && delay <= 0);

    useEffect(() => {
        if (!active) {
            setVisible(false);
            return undefined;
        }
        if (delay <= 0) {
            setVisible(true);
            return undefined;
        }
        const timer = setTimeout(() => setVisible(true), delay);
        return () => clearTimeout(timer);
    }, [active, delay]);

    return visible;
};

/** 내용이 들어올 자리를 표시하는 회색 블록. */
export const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, className = '', style }) => (
    <div
        className={`${styles.skeleton} ${className}`}
        style={{ width, height, borderRadius: radius, ...style }}
        aria-hidden="true"
    />
);
SkeletonBlock.displayName = 'SkeletonBlock';

/**
 * 목록 영역의 조회 상태 표시.
 *
 * - loading : delay 이후 스켈레톤 count 개 (목록 그리드 안에 그대로 끼워 넣는다)
 * - empty   : 데이터 없음 문구
 * - error   : 실패 문구 + 다시 시도 버튼
 * - 그 외   : 아무것도 그리지 않는다
 */
const LoadState = ({
    status,
    count = 3,
    height = 160,
    delay = 250,
    skeletonClassName = '',
    emptyText = '조회된 데이터가 없습니다.',
    errorText = '데이터를 불러오지 못했습니다.',
    retryText = '다시 시도',
    onRetry,
}) => {
    const showSkeleton = useDelayedFlag(status === 'loading', delay);

    if (status === 'loading') {
        if (!showSkeleton) return null;
        return (
            <>
                {Array.from({ length: count }, (_, idx) => (
                    <SkeletonBlock key={idx} height={height} radius={12} className={skeletonClassName} />
                ))}
                <span className={styles.srOnly} role="status">
                    불러오는 중
                </span>
            </>
        );
    }

    if (status === 'empty' || status === 'error') {
        const isError = status === 'error';
        return (
            <div className={styles.message} role={isError ? 'alert' : 'status'}>
                <p>{isError ? errorText : emptyText}</p>
                {isError && onRetry ? (
                    <button type="button" className={styles.retry} onClick={onRetry}>
                        {retryText}
                    </button>
                ) : null}
            </div>
        );
    }

    return null;
};
LoadState.displayName = 'LoadState';

/** 제목 옆 등에 붙이는 작은 진행 표시. active 이고 delay 가 지나야 보인다. */
export const LoadingText = ({ active = true, text = '불러오는 중', delay = 250, className = '', style }) => {
    const visible = useDelayedFlag(active, delay);
    if (!visible) return null;
    return (
        <span className={`${styles.loadingText} ${className}`} style={style} role="status">
            <span className={styles.dot} aria-hidden="true" />
            {text}
        </span>
    );
};
LoadingText.displayName = 'LoadingText';

export default LoadState;
